import { frameId } from "../assets/manifest.ts";
import { frameSizeOf, initialFootprint, stateFootprint } from "../compile/behaviour.ts";
import type { CompileContext } from "../compile/context.ts";
import { footprintBlocks, type PropFootprint } from "../compile/footprint.ts";
import { blockingFootprint } from "../compile/prop-footprint.ts";
import type { MapDocument, PropInstance } from "../document/types.ts";
import type { ValidationIssue } from "./adapter.ts";
import { groupAlignments, keyOf, moveWords, offCellProps, placedProps, reported, type CellGrid, type FrameWidthOf, type PropMeasure } from "./prop-align.ts";

/**
 * What the editor says of props off the collision cells, and how it measures them from a compile context
 * (ADR-040 amendments of 2026-10-03, "props stand on the cells", and of 2026-10-04, P1.6-T88): the measure
 * that sizes a frame and counts the cells a prop blocks, and the Issues list's warnings, one for each prop
 * that collides, stands off the cells and is not kept as placed.
 */

/**
 * The measure a compile context gives: a frame's size as the compile sizes it, and the cells a prop blocks as
 * the footprint panel counts them, a plain prop by its frame's footprint and a behaviour prop by its initial
 * state's; a vertical mirror, a frame with no footprint and a state that cannot be resolved block nothing. A
 * behaviour prop collides when any state the compile stamps for it blocks a cell, the initial one alone for a
 * prop driven by presence, so a gate placed open still collides by its closed state.
 *
 * @param context The compile context.
 * @param grid The cells' geometry.
 * @returns The measure.
 */
export function contextPropMeasure(context: CompileContext, grid: CellGrid): PropMeasure {
  const cell = grid.tileSize / grid.cellsPerTile;
  /**
   * The cells a footprint blocks with its frame on a cell boundary.
   *
   * @param footprint The footprint, or none.
   * @returns How many cells, 0 for no footprint.
   */
  const count = (footprint: PropFootprint | null | undefined): number => (footprint === null || footprint === undefined ? 0 : footprintBlocks(footprint, cell).length);
  /**
   * The cells a prop blocks as placed: a plain prop by its own footprint or its frame's (`blockingFootprint`),
   * a behaviour prop by its initial state's.
   *
   * @param prop The prop.
   * @returns How many cells, 0 for a vertical mirror, a frame with no footprint and a state that cannot be
   *   resolved.
   */
  const blocks = (prop: PropInstance): number => {
    if (prop.flipY) {
      return 0;
    }
    if (prop.behaviour === undefined) {
      return count(blockingFootprint(prop, context, grid.tileSize));
    }
    try {
      return count(initialFootprint(prop, context, grid.tileSize));
    } catch {
      return 0;
    }
  };
  return {
    sizeOf: (frame) => frameSizeOf(context, frameId(frame.tileset, null, frame.tile)),
    blocks,
    collides: (prop) => {
      if (blocks(prop) > 0) {
        return true;
      }
      const type = prop.flipY ? undefined : context.objectTypes.find((candidate) => candidate.type === prop.behaviour?.type);
      if (type === undefined || type.inside !== undefined) {
        return false;
      }
      return type.states.some((state) => {
        try {
          return count(stateFootprint(prop, context, grid.tileSize, state.id)) > 0;
        } catch {
          return false;
        }
      });
    },
  };
}

/**
 * The words every alignment warning ends its fix with: the author may accept the placement instead.
 */
const OR_KEEP = ", or Keep as placed if it stands there on purpose";

/**
 * The warnings for props off the collision cells, one for each that collides and is not kept as placed: the
 * Issues list shows them and offers a fix on each line and, beside it, Keep as placed (`keep-prop-placement`),
 * each distance said as the way to the prop's nearest cell corner (`moveWords`). A prop in a group the align
 * leaves because a piece of it is kept as placed is told so and offered the move of that prop alone. A prop whose group
 * stands off the cells is fixed by aligning the group (`align-prop`); a member that move leaves off the cells
 * is told the group moves and by what it then stays off, never that the align puts it on them. A prop
 * off the cells inside a group that already stands on them keeps its place against the other pieces, so its
 * line names the residual and offers only the explicit move of that prop alone (`align-prop-alone`). They
 * never stop a compile, since such a prop still blocks exactly the footprint panel's cells; only its art lies
 * beside them.
 *
 * @param map The map.
 * @param measure Frame sizes and blocked cells.
 * @param grid The cells' geometry.
 * @returns The warnings, in the groups' order.
 */
export function cellAlignmentIssues(map: MapDocument, measure: PropMeasure, grid: CellGrid): ValidationIssue[] {
  const widthOf: FrameWidthOf = (frame) => measure.sizeOf(frame)?.w;
  const placed = new Map(placedProps(map).map(({ layerId, prop }) => [keyOf(layerId, prop.id), prop]));
  const own = new Map(
    offCellProps(map, widthOf, grid)
      .filter((move) => reported(placed.get(keyOf(move.layerId, move.propId)) as PropInstance, measure))
      .map((move) => [keyOf(move.layerId, move.propId), move]),
  );
  if (own.size === 0) {
    return [];
  }
  return groupAlignments(map, measure, grid).flatMap((group) => {
    const settled = group.dx === 0 && group.dy === 0;
    const left = new Map(group.residuals.map((residual) => [keyOf(residual.layerId, residual.propId), residual]));
    return group.members.flatMap((member): ValidationIssue[] => {
      const move = own.get(keyOf(member.layerId, member.propId));
      if (move === undefined) {
        return [];
      }
      const far = `its nearest cell corner ${moveWords(move.dx, move.dy)}`;
      const located = { mapId: map.id, kind: "prop.off-cells", layerId: member.layerId, propId: member.propId, severity: "warning" as const, alternative: "keep-prop-placement" as const };
      const alone = `nudge it by hand, align it alone and it moves against the pieces it is composed with${OR_KEEP}`;
      if (settled) {
        return [{ ...located, remedy: "align-prop-alone", message: `prop ${member.propId} is off the collision cells inside a group, ${far}, so its collision lies that far from its art while its group stands on the cells; ${alone}` }];
      }
      if (group.kept > 0) {
        return [{ ...located, remedy: "align-prop-alone", message: `prop ${member.propId} stands off the collision cells, ${far}, so its collision lies that far from its art, in a group that Align props to collision cells leaves where it stands since a piece of it is kept as placed; ${alone}` }];
      }
      const stays = left.get(keyOf(member.layerId, member.propId));
      const fix = stays === undefined ? `moves it onto them${group.members.length === 1 ? "" : " with the pieces it is composed with"}` : `moves it with the pieces it is composed with, and it then stays off the cells, its nearest cell corner ${moveWords(stays.dx, stays.dy)}`;
      return [{ ...located, remedy: "align-prop", message: `prop ${member.propId} stands off the collision cells, ${far}, so its collision lies that far from its art; Align props to collision cells in the Edit menu ${fix}${OR_KEEP}` }];
    });
  });
}
